import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ChatFile } from './types';

type PresignedResponse = { url: string; location: string; uploadToken: string };

async function uploadOne(conversationId: string, file: File): Promise<ChatFile> {
  const { data: presigned } = await api.post<PresignedResponse>(
    `conversations/${conversationId}/files/presigned-upload-url`,
    { fileName: file.name },
  );

  const s3Response = await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!s3Response.ok) {
    throw new Error(`Failed to upload to storage: ${s3Response.status} ${s3Response.statusText}`);
  }

  const { data } = await api.post<ChatFile>(`conversations/${conversationId}/files`, {
    uploadToken: presigned.uploadToken,
    fileSize: file.size,
    fileType: file.type,
  });
  return data;
}

// Manages a conversation's pending chat files: uploads on attach (presigned URL + upload-token
// flow, in parallel) and deletes on removal. Delete rejects on failure (after capturing to Sentry)
// so the caller can restore the pending card and notify, keeping local state in sync with the
// server: a surviving chat_file row is still surfaced to Siesta on the next send.
export function useChatFiles(conversationId: string) {
  const { mutateAsync, isPending } = useMutation<ChatFile[], Error, File[]>({
    mutationFn: async files => {
      const results = await Promise.allSettled(files.map(file => uploadOne(conversationId, file)));

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          notifyAndLogError(
            'Upload failed',
            `Could not attach ${files[index].name}`,
            result.reason,
          );
        }
      });

      return results
        .filter(
          (result): result is PromiseFulfilledResult<ChatFile> => result.status === 'fulfilled',
        )
        .map(result => result.value);
    },
  });

  const deleteChatFile = (fileId: string) =>
    api.delete(`conversations/${conversationId}/files/${fileId}`).catch(error => {
      Sentry.captureException(error);
      throw error;
    });

  return { uploadChatFiles: mutateAsync, isUploading: isPending, deleteChatFile };
}
